
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AddVideoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  techniqueId: string;
  onVideoAdded: () => Promise<void>;
}

export const AddVideoDialog: React.FC<AddVideoDialogProps> = ({
  open,
  onOpenChange,
  techniqueId,
  onVideoAdded
}) => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!youtubeUrl.trim()) {
      toast({
        title: "Error",
        description: "Please enter a YouTube URL",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const insertData = {
        technique_id: techniqueId,
        user_id: user.id,
        youtube_url: youtubeUrl.trim(),
        title: title.trim() || null,
        notes: notes.trim() || null
      };

      console.log('Inserting video data:', insertData);

      const { error } = await supabase
        .from('youtube_videos')
        .insert(insertData);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Video added successfully"
      });

      // Reset form
      setYoutubeUrl('');
      setTitle('');
      setNotes('');
      
      // Refresh videos list
      await onVideoAdded();
      
      // Close dialog
      onOpenChange(false);
    } catch (error: unknown) {
      console.error('Error adding video:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add video",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add YouTube Video</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="youtube-url">YouTube URL</Label>
            <Input
              id="youtube-url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              required
            />
          </div>
          <div>
            <Label htmlFor="video-title">Title (optional)</Label>
            <Input
              id="video-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Video title"
            />
          </div>
          <div>
            <Label htmlFor="video-notes">Notes (optional)</Label>
            <Textarea
              id="video-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this video..."
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Video'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
